import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { memo, useMemo, useState } from 'react';
import { IAlert24hData } from '~/utils/interface/dashboard';
import { IUser } from '~/utils/interface/user';

type Alert24hSummaryProps = {
  data?: Array<IAlert24hData>;
  currentUser?: IUser;
};

function CustomTooltip({ active, payload, label, t }: any) {
  if (!active || !payload?.length) return null;

  return (
    <Box className='rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-lg'>
      <Box className='text-sm font-semibold text-slate-900'>{label + 'h'}</Box>
      <Box className='text-sm text-slate-600'>
        {t('alert24h.text')}
        <Typography component={'span'} className='font-semibold text-slate-900'>
          {payload?.[0].value}
        </Typography>
      </Box>
    </Box>
  );
}

const CustomActiveDot = (props: any) => {
  const { cx, cy, value } = props;

  if (cx === undefined || cy === undefined) return null;

  return (
    <g>
      <circle cx={cx} cy={cy} r={4} fill='#fff' stroke='#d88712' strokeWidth={2} />
      <g transform={`translate(${cx}, ${cy - 35})`}>
        <rect x='-15' y='0' width='30' height='20' rx='4' fill='#d88712' />
        <text x='0' y='14' fill='#fff' textAnchor='middle' fontSize='12' fontWeight='bold'>
          {value}
        </text>
        <path d='M-4 20 L0 24 L4 20 Z' fill='#d88712' />
      </g>
    </g>
  );
};

const Alert24hSummary = (props: Alert24hSummaryProps) => {
  const { t } = useTranslation('admin/home');

  const [activeIndex, setActiveIndex] = useState<number>(0);

  const safeData = useMemo(() => {
    return Array.isArray(props.data) && props.data.length ? props.data : [];
  }, [props.data]);

  const activePoint = safeData[activeIndex] ?? safeData?.[0];

  const [isHovering, setIsHovering] = useState<boolean>(false);

  const yMax = useMemo(() => {
    const max = Math.max(...safeData.map((item) => Number(item.total) || 0), 0);
    return Math.ceil((max + 40) / 50) * 50;
  }, [safeData]);

  return (
    <Box className='rounded-xl bg-white flex flex-col'>
      <Typography className='bg-[#123B6D] text-white font-semibold text-xl rounded-t-xl p-3'>
        {t('alert24h.title')}
      </Typography>
      <Box
        style={{
          width: '100%',
          height: 320,
        }}
      >
        <ResponsiveContainer>
          <ComposedChart
            data={safeData}
            margin={{ top: 25, right: 18, bottom: 20, left: 0 }}
            onMouseMove={(state) => {
              if (state?.activeTooltipIndex) {
                setActiveIndex(Number(state.activeTooltipIndex));
              }
            }}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <defs>
              <linearGradient id='softFill' x1='0' y1='0' x2='0' y2='1'>
                <stop offset='0%' stopColor={'#d88712'} stopOpacity={0.16} />
                <stop offset='100%' stopColor={'#d88712'} stopOpacity={0.02} />
              </linearGradient>
            </defs>

            <CartesianGrid strokeDasharray='3 3' vertical={false} stroke='#e5e7eb' />
            <XAxis
              dataKey='hour'
              dy={15}
              tick={{ fontSize: 12, fill: '#94a3b8' }}
              tickFormatter={(value) => `${value}h`}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              domain={[0, yMax]}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#94a3b8' }}
              width={40}
            />
            <Tooltip content={<CustomTooltip t={t} />} cursor={false} />
            <Area type='monotone' dataKey='total' stroke='none' fill='url(#softFill)' />
            <Line type='monotone' dataKey='total' stroke={'#d88712'} strokeWidth={2} dot={false} activeDot={false} />
            <Line
              type='monotone'
              dataKey='total'
              stroke={'#d88712'}
              strokeWidth={2}
              dot={false}
              activeDot={<CustomActiveDot />}
            />

            {isHovering && activePoint && (
              <>
                <ReferenceLine x={activePoint.hour} stroke={'#d88712'} strokeDasharray='4 4' strokeOpacity={0.65} />
                <ReferenceDot
                  x={activePoint.hour}
                  y={activePoint.total}
                  r={4}
                  fill={'#fff'}
                  stroke='#d88712'
                  strokeWidth={1}
                />
              </>
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default memo(Alert24hSummary);
